import {
  PlayCircleIcon,
  SwitchHorizontal01Icon,
} from '@langchain/untitled-ui-icons';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { FeedbackChips } from '@/components/FeedbackChips';
import { DetailPageTitle } from '@/components/PageTitle';
import { LatencyChip } from '@/components/RunLatencyChip';
import { Badge, Button, GroupedTabs, Text, Tooltip } from '@/design-system';
import TokenIcon from '@/icons/TokenIcon.svg?react';
import { ResourceType, RunStatsSchema } from '@/types/schema';

dayjs.extend(duration);

interface ConversationHeaderV2Props {
  conversationId: string | null;
  stats: RunStatsSchema;
  graphUrl?: string | null;
  onNavigateToGraph: () => void;
  editViewComponent: React.ReactNode;
  activeTab: 'turn' | 'trace';
  setActiveTab: (tab: 'turn' | 'trace') => void;
  totalLatency: number;
}

const formatter = new Intl.NumberFormat('en-US', {});
const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 20,
});

function formatTime(seconds?: number): string {
  if (seconds == null) return '--';
  const dur = dayjs.duration(seconds, 'seconds');

  if (seconds < 1) {
    return `${Math.round(seconds * 1000)}ms`;
  } else if (seconds < 60) {
    return `${seconds.toFixed(2)}s`;
  } else {
    return dur.format('m[m] s[s]');
  }
}

export function ConversationHeaderV2({
  conversationId,
  stats,
  graphUrl,
  onNavigateToGraph,
  editViewComponent,
  activeTab,
  setActiveTab,
  totalLatency,
}: ConversationHeaderV2Props) {
  return (
    <div className="flex flex-col gap-2 border-b border-secondary p-4">
      <div className="flex flex-none items-center justify-between">
        <DetailPageTitle
          title={
            <div className="flex items-center gap-2">
              <Text variant="h3" as="h1" weight="semibold">
                Thread
              </Text>
              <Text variant="md" color="secondary" weight="medium">
                {conversationId}
              </Text>
            </div>
          }
          id={conversationId ?? undefined}
          resourceType={ResourceType.Conversation}
          rightDecorator={
            /* Stats */
            <div className="ml-1 flex items-center gap-2 text-sm">
              {stats.run_count && (
                <Badge
                  rounded="sm"
                  color="plain"
                  size="sm"
                  leftDecorator={SwitchHorizontal01Icon}
                >
                  {formatter.format(stats.run_count)}
                </Badge>
              )}
              {stats.total_tokens && (
                <Tooltip
                  title={
                    <div className="flex flex-col gap-2 p-2">
                      <Text variant="h4">Total tokens</Text>
                      <Text>
                        {formatter.format(stats.total_tokens)} /{' '}
                        {usdFormatter.format(stats.total_cost ?? 0)}
                      </Text>
                    </div>
                  }
                >
                  <Badge
                    rounded="sm"
                    size="sm"
                    color="plain"
                    className="cursor-pointer gap-1"
                    leftDecorator={TokenIcon}
                  >
                    {formatter.format(stats.total_tokens)}
                  </Badge>
                </Tooltip>
              )}
              {totalLatency && (
                <Tooltip
                  title={
                    <div className="flex flex-col gap-2 p-2">
                      <Text variant="h4">Latency</Text>
                      <div className="flex items-center gap-1">
                        <LatencyChip
                          latency={stats.latency_p50}
                          label="P50: "
                          percentiles={{}}
                        />
                        <LatencyChip
                          latency={stats.latency_p99}
                          label="P99: "
                          percentiles={{}}
                        />
                      </div>
                    </div>
                  }
                >
                  <Badge
                    rounded="sm"
                    size="sm"
                    color="plain"
                    className="cursor-pointer gap-1"
                  >{`${formatTime(totalLatency)} total`}</Badge>
                </Tooltip>
              )}
            </div>
          }
        />
        {/* Right side - Actions */}
        <div className="flex items-center gap-2">
          <Tooltip
            title={
              <div className="flex items-center gap-2">
                <div className="h-5 w-5 rounded-md border border-secondary bg-quaternary text-center font-mono">
                  T
                </div>
                Toggle view
              </div>
            }
          >
            <div>
              <GroupedTabs
                value={activeTab}
                onChange={(v) => setActiveTab(v)}
                options={[
                  { value: 'turn', display: 'Turn' },
                  { value: 'trace', display: 'Trace' },
                ]}
              />
            </div>
          </Tooltip>
          {graphUrl != null && (
            <Button
              size="sm"
              variant="outlined"
              color="primary"
              leftDecorator={PlayCircleIcon}
              onClick={onNavigateToGraph}
            >
              LangGraph Studio
            </Button>
          )}
          {editViewComponent}
        </div>
      </div>
      {stats.feedback_stats && (
        <div className="flex flex-wrap items-center gap-2">
          <FeedbackChips feedbackStats={stats.feedback_stats} />
        </div>
      )}
    </div>
  );
}
